/**
 * Player Development PDF — Letter portrait, light print.
 * Miller QA: age-stacked G1-G4, G5 donut, header align, ASCII-only text.
 */

export type PdPdfBandVals = {
  use: number;
  visits: number;
  active: number;
  newUsers: number;
};

export type PdPdfWeekPoint = {
  week: string;
  weekStart: string;
  use: number;
  visits: number;
  activeUseAndVisit: number;
  newUsers: number;
  byBand: Record<string, PdPdfBandVals>;
};

export type PdPdfMixSlice = { name: string; value: number; color: string };

export type PdPdfKpis = {
  players: number;
  activeUseAndVisit: number;
  usedThisWeek: number;
  visitedThisWeek: number;
  newUsers: number;
  useThisWeek: number;
  visitsThisWeek: number;
  useWowPct: number | null;
  useMomPct: number | null;
  visitsWowPct: number | null;
  visitsMomPct: number | null;
  useLastMonth: number;
  useLastWeek: number;
  useMonthToDate: number;
  visitsLastMonth: number;
  visitsLastWeek: number;
  visitsMonthToDate: number;
};

export type PdPdfWindows = {
  use: number[];
  visits: number[];
};

export type PdPdfInput = {
  casinoLabel: string;
  fromWeek: string;
  toWeek: string;
  fromWeekStart?: string;
  toWeekStart?: string;
  ageLabel: string;
  genderLabel: string;
  generatedAt: Date;
  kpis: PdPdfKpis;
  ageBands: string[];
  bandColors: string[]; // hex, parallel to ageBands
  weekSeries: PdPdfWeekPoint[];
  latestWeekLabel: string;
  g5Week: string;
  /** G5 views: Age / Gender / Gender counts by Age Range */
  g5Views: Array<{ label: string; slices: PdPdfMixSlice[] }>;
};

const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 36;
const CONTENT_W = PAGE_W - MARGIN * 2;

const COL = {
  ink: [0.12, 0.14, 0.18] as RGB,
  muted: [0.4, 0.43, 0.48] as RGB,
  soft: [0.55, 0.58, 0.62] as RGB,
  line: [0.78, 0.8, 0.84] as RGB,
  zebra: [0.97, 0.98, 0.99] as RGB,
  headerBg: [0.12, 0.14, 0.18] as RGB,
  gold: [0.85, 0.65, 0.13] as RGB,
  white: [1, 1, 1] as RGB,
  page: [0.96, 0.97, 0.98] as RGB,
};

type RGB = [number, number, number];

const hexToRgb = (hex: string): RGB => {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

/** ASCII-only for Helvetica Type1 (no ? corruption). */
const escapdPdf = (s: string) =>
  String(s ?? '')
    .normalize('NFKD')
    .replace(/[\u2013\u2014\u2212]/g, '-')
    .replace(/[\u2192\u2190\u00B7\u2022]/g, '-')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');

const fmtInt = (n: number) => Math.round(Number(n) || 0).toLocaleString('en-US');
const fmtAvg = (n: number) =>
  (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });

const rgbOp = (c: RGB, op: 'rg' | 'RG' = 'rg') => `${c[0].toFixed(3)} ${c[1].toFixed(3)} ${c[2].toFixed(3)} ${op}`;

class PdPdfDoc {
  private pages: string[] = [];
  private content: string[] = [];
  private y = PAGE_H - MARGIN;
  private pageNo = 0;
  private filename = '';
  private casino = '';
  private range = '';

  setMeta(filename: string, casino: string, range: string) {
    this.filename = filename;
    this.casino = casino;
    this.range = range;
  }

  private flush() {
    if (!this.content.length) return;
    this.footer();
    this.pages.push(this.content.join('\n'));
    this.content = [];
  }

  newPage(continued = false) {
    this.flush();
    this.pageNo += 1;
    this.content.push(rgbOp(COL.page), `0 0 ${PAGE_W} ${PAGE_H} re`, 'f');
    if (continued) {
      const h = 28;
      const top = PAGE_H - 16;
      this.content.push(rgbOp(COL.headerBg), `0 ${top - h} ${PAGE_W} ${h + 16} re`, 'f');
      this.text(MARGIN, top - 18, 'Player Development - continued', 11, true, COL.white);
      this.text(PAGE_W - MARGIN - 210, top - 18, `${this.casino} - ${this.range}`, 8, false, [
        0.75, 0.78, 0.82,
      ]);
      this.y = top - h - 14;
    } else {
      this.y = PAGE_H - MARGIN;
    }
  }

  /** Dark header: left brand+title, right meta block vertically aligned. */
  mainHeader(casino: string, range: string, generated: string) {
    const h = 56;
    const top = PAGE_H;
    this.content.push(rgbOp(COL.headerBg), `0 ${top - h} ${PAGE_W} ${h} re`, 'f');
    // left brand
    this.content.push(rgbOp(COL.gold), `${MARGIN} ${top - 28} 7 7 re`, 'f');
    this.text(MARGIN + 12, top - 27, 'SlotCheck', 9, true, COL.white);
    this.text(MARGIN, top - 46, 'Player Development', 18, true, COL.white);
    // right meta - top-aligned with SlotCheck row, stacked
    const rx = PAGE_W - MARGIN - 200;
    this.text(rx, top - 22, casino, 9, true, COL.white);
    this.text(rx, top - 34, range, 8, false, [0.8, 0.82, 0.86]);
    this.text(rx, top - 46, generated, 8, false, [0.8, 0.82, 0.86]);
    this.y = top - h - 12;
  }

  private footer() {
    const total = 2;
    this.content.push(
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} 32 m`,
      `${PAGE_W - MARGIN} 32 l`,
      'S'
    );
    this.text(MARGIN, 20, 'Source: SlotCheck Player Development - Cognito-gated report', 7, false, COL.soft);
    const right =
      this.pageNo === 1
        ? `Page 1 of ${total} - ${this.filename}`
        : `Page ${this.pageNo} of ${total}`;
    this.text(PAGE_W - MARGIN - 220, 20, right, 7, false, COL.soft);
  }

  text(x: number, y: number, str: string, size: number, bold = false, color: RGB = COL.ink) {
    this.content.push(
      rgbOp(color),
      'BT',
      `${bold ? '/F2' : '/F1'} ${size} Tf`,
      `1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm`,
      `(${escapdPdf(str)}) Tj`,
      'ET'
    );
  }

  ensure(space: number) {
    if (this.y - space < 48) this.newPage(true);
  }

  section(title: string, helper: string) {
    this.ensure(34);
    this.text(MARGIN, this.y, title, 12, true);
    this.y -= 13;
    if (helper) {
      this.text(MARGIN, this.y, helper, 8, false, COL.muted);
      this.y -= 11;
    }
    this.y -= 2;
  }

  scopeRow(items: Array<{ label: string; value: string }>) {
    this.ensure(44);
    const h = 40;
    const top = this.y;
    this.content.push(
      rgbOp(COL.white),
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'f',
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'S'
    );
    const colW = CONTENT_W / items.length;
    items.forEach((it, i) => {
      const x = MARGIN + i * colW + 8;
      this.text(x, top - 14, it.label.toUpperCase(), 7, true, COL.muted);
      this.text(x, top - 30, it.value, 10, true);
    });
    this.y = top - h - 12;
  }

  kpiRow(items: Array<{ label: string; value: string; hint: string }>) {
    this.ensure(70);
    const h = 62;
    const gap = 8;
    const colW = (CONTENT_W - gap * (items.length - 1)) / items.length;
    const top = this.y;
    items.forEach((it, i) => {
      const x = MARGIN + i * (colW + gap);
      this.content.push(
        rgbOp(COL.white),
        `${x} ${top - h} ${colW} ${h} re`,
        'f',
        rgbOp(COL.line, 'RG'),
        '0.5 w',
        `${x} ${top - h} ${colW} ${h} re`,
        'S',
        rgbOp(COL.gold),
        `${x} ${top - 3} ${colW} 3 re`,
        'f'
      );
      this.text(x + 8, top - 16, it.label, 7, true, COL.muted);
      this.text(x + 8, top - 34, it.value, 15, true);
      this.text(x + 8, top - 50, it.hint, 6, false, COL.soft);
    });
    this.y = top - h - 12;
  }

  /** Compact age-band color legend under a chart. */
  private legend(x: number, y: number, bands: string[], colors: string[]) {
    let cx = x;
    bands.forEach((b, i) => {
      const c = hexToRgb(colors[i] || '#888888');
      this.content.push(rgbOp(c), `${cx} ${y - 1} 6 6 re`, 'f');
      this.text(cx + 8, y, b, 6, false, COL.muted);
      cx += 8 + Math.min(42, b.length * 4 + 10);
      if (cx > x + CONTENT_W / 2 - 20 && i < bands.length - 1) {
        // wrap not needed in half-width cards - shrink
      }
    });
  }

  private yTicks(plotX: number, plotY: number, plotW: number, plotH: number, max: number) {
    const ticks = 3;
    for (let i = 0; i <= ticks; i++) {
      const t = i / ticks;
      const yy = plotY + t * plotH;
      const val = Math.round(max * t);
      this.content.push(
        rgbOp(COL.line, 'RG'),
        '0.3 w',
        `${plotX} ${yy.toFixed(2)} m`,
        `${(plotX + plotW).toFixed(2)} ${yy.toFixed(2)} l`,
        'S'
      );
      this.text(plotX - 2, yy - 2, String(val), 6, false, COL.soft);
    }
  }

  private stackedBars(
    plotX: number,
    plotY: number,
    plotW: number,
    plotH: number,
    series: PdPdfWeekPoint[],
    bands: string[],
    colors: string[],
    key: 'use' | 'visits' | 'active' | 'newUsers'
  ) {
    const totals = series.map((m) =>
      bands.reduce((s, b) => s + (m.byBand[b]?.[key] || 0), 0)
    );
    const max = Math.max(...totals, 1);
    const n = Math.max(series.length, 1);
    const gap = 1.5;
    const barW = Math.max(3, (plotW - 28 - gap * (n - 1)) / n);
    const baseX = plotX + 26;
    this.yTicks(baseX, plotY, plotW - 28, plotH, max);
    series.forEach((m, i) => {
      let y0 = plotY;
      const bx = baseX + i * (barW + gap);
      bands.forEach((b, bi) => {
        const v = m.byBand[b]?.[key] || 0;
        if (v <= 0) return;
        const bh = (v / max) * (plotH - 2);
        this.content.push(
          rgbOp(hexToRgb(colors[bi] || '#888')),
          `${bx.toFixed(2)} ${y0.toFixed(2)} ${barW.toFixed(2)} ${Math.max(0.4, bh).toFixed(2)} re`,
          'f'
        );
        y0 += bh;
      });
    });
    // sparse month labels
    const step = Math.max(1, Math.ceil(n / 4));
    series.forEach((m, i) => {
      if (i % step !== 0 && i !== n - 1) return;
      const bx = baseX + i * (barW + gap);
      this.text(bx, plotY - 8, m.week.replace(/^[0-9]{4}-/, ''), 5, false, COL.soft);
    });
  }

  /** Approximate donut with triangular wedges + white hole. */
  private donut(
    cx: number,
    cy: number,
    rOuter: number,
    rInner: number,
    slices: PdPdfMixSlice[],
    centerLabel: string
  ) {
    const total = slices.reduce((s, d) => s + d.value, 0) || 1;
    let angle = -Math.PI / 2;
    const wedge = (a0: number, a1: number, color: RGB) => {
      const steps = Math.max(8, Math.ceil(((a1 - a0) * 24) / Math.PI));
      const pts: Array<[number, number]> = [[cx, cy]];
      for (let i = 0; i <= steps; i++) {
        const a = a0 + ((a1 - a0) * i) / steps;
        pts.push([cx + Math.cos(a) * rOuter, cy + Math.sin(a) * rOuter]);
      }
      this.content.push(rgbOp(color));
      pts.forEach(([x, y], i) => {
        this.content.push(i === 0 ? `${x.toFixed(2)} ${y.toFixed(2)} m` : `${x.toFixed(2)} ${y.toFixed(2)} l`);
      });
      this.content.push('f');
    };
    slices.forEach((sl) => {
      const span = (sl.value / total) * Math.PI * 2;
      wedge(angle, angle + span, hexToRgb(sl.color));
      angle += span;
    });
    // hole
    this.content.push(rgbOp(COL.white));
    const holeSteps = 36;
    for (let i = 0; i <= holeSteps; i++) {
      const a = (i / holeSteps) * Math.PI * 2;
      const x = cx + Math.cos(a) * rInner;
      const y = cy + Math.sin(a) * rInner;
      this.content.push(i === 0 ? `${x.toFixed(2)} ${y.toFixed(2)} m` : `${x.toFixed(2)} ${y.toFixed(2)} l`);
    }
    this.content.push('f');
    this.text(cx - 18, cy + 2, centerLabel, 9, true);
    this.text(cx - 18, cy - 10, 'players', 7, false, COL.muted);
  }

  trendsGrid(series: PdPdfWeekPoint[], bands: string[], colors: string[]) {
    this.ensure(230);
    const top = this.y;
    const gap = 8;
    const cardW = (CONTENT_W - gap) / 2;
    const cardH = 108;

    const card = (
      x: number,
      yTop: number,
      title: string,
      draw: (px: number, py: number, pw: number, ph: number) => void
    ) => {
      this.content.push(
        rgbOp(COL.white),
        `${x} ${yTop - cardH} ${cardW} ${cardH} re`,
        'f',
        rgbOp(COL.line, 'RG'),
        '0.5 w',
        `${x} ${yTop - cardH} ${cardW} ${cardH} re`,
        'S'
      );
      this.text(x + 6, yTop - 12, title, 8, true);
      this.legend(x + 6, yTop - 22, bands, colors);
      draw(x + 6, yTop - cardH + 14, cardW - 12, cardH - 42);
    };

    card(MARGIN, top, 'G1 - Use over weeks', (x, y, w, h) =>
      this.stackedBars(x, y, w, h, series, bands, colors, 'use')
    );
    card(MARGIN + cardW + gap, top, 'G2 - Visits over weeks', (x, y, w, h) =>
      this.stackedBars(x, y, w, h, series, bands, colors, 'visits')
    );
    card(MARGIN, top - cardH - gap, 'G3 - Active Use & Visit', (x, y, w, h) =>
      this.stackedBars(x, y, w, h, series, bands, colors, 'active')
    );
    card(MARGIN + cardW + gap, top - cardH - gap, 'G4 - New Users', (x, y, w, h) =>
      this.stackedBars(x, y, w, h, series, bands, colors, 'newUsers')
    );
    this.y = top - cardH * 2 - gap - 12;
  }

  mixDonut(month: string, slices: PdPdfMixSlice[], modeLabel: string) {
    // Donut ABOVE, legend BELOW full width
    const legendRows = Math.max(1, slices.length);
    const legendCols = legendRows > 6 ? 3 : legendRows > 4 ? 2 : 1;
    const rowsPerCol = Math.ceil(legendRows / legendCols);
    const donutBlock = 86;
    const legendH = rowsPerCol * 12 + 8;
    const h = 34 + donutBlock + legendH;
    this.ensure(h + 16);
    const top = this.y;
    this.content.push(
      rgbOp(COL.white),
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'f',
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'S'
    );
    this.text(MARGIN + 8, top - 14, `Mix for ${month} UTC (${modeLabel})`, 9, true);
    this.text(MARGIN + 8, top - 26, 'Latest-week composition.', 7, false, COL.muted);

    const total = slices.reduce((s, d) => s + d.value, 0);
    const cx = MARGIN + CONTENT_W / 2;
    const cy = top - 34 - donutBlock / 2;
    if (slices.length && total > 0) {
      this.donut(cx, cy, 34, 20, slices, fmtInt(total));
    } else {
      this.text(cx - 20, cy, 'No data', 9, false, COL.muted);
    }

    const legendTop = top - 34 - donutBlock - 2;
    const colW = CONTENT_W / legendCols;
    slices.forEach((d, i) => {
      const col = Math.floor(i / rowsPerCol);
      const row = i % rowsPerCol;
      const lx = MARGIN + 10 + col * colW;
      const ly = legendTop - row * 12;
      const pct = total ? ((d.value / total) * 100).toFixed(1) : '0.0';
      this.content.push(rgbOp(hexToRgb(d.color)), `${lx} ${ly - 1} 7 7 re`, 'f');
      this.text(lx + 12, ly, `${d.name}  ${fmtInt(d.value)} · ${pct}%`, 8);
    });
    this.y = top - h - 12;
  }


  windowPair(k: PdPdfKpis) {
    this.ensure(130);
    const top = this.y;
    const gap = 8;
    const cardW = (CONTENT_W - gap) / 2;
    const cardH = 118;
    const labels = ['Last Mo', 'Last Wk', 'MTD', 'This Wk'];
    const paint = (x: number, title: string, vals: number[], color: RGB, wow: string, mom: string) => {
      this.content.push(
        rgbOp(COL.white),
        `${x} ${top - cardH} ${cardW} ${cardH} re`,
        'f',
        rgbOp(COL.line, 'RG'),
        '0.5 w',
        `${x} ${top - cardH} ${cardW} ${cardH} re`,
        'S'
      );
      this.text(x + 6, top - 12, title, 8, true);
      const sum = vals.reduce((s, v) => s + v, 0) || 1;
      const max = Math.max(...vals, 1);
      const plotX = x + 10;
      const plotY = top - cardH + 28;
      const plotH = 52;
      const plotW = cardW - 20;
      const barW = (plotW - 12) / 4;
      vals.forEach((v, i) => {
        const bh = (v / max) * plotH;
        const bx = plotX + i * barW;
        this.content.push(rgbOp(color), `${bx.toFixed(2)} ${plotY.toFixed(2)} ${(barW - 6).toFixed(2)} ${Math.max(0.6, bh).toFixed(2)} re`, 'f');
        this.text(bx, plotY - 8, labels[i], 5, false, COL.soft);
        this.text(bx, plotY + bh + 6, String(Math.round(v)), 5, false, COL.muted);
      });
      this.text(x + 6, top - cardH + 16, wow, 7, true, COL.muted);
      this.text(x + cardW / 2, top - cardH + 16, mom, 7, true, COL.muted);
    };
    const fmt = (p: number | null, kind: string) => (p == null ? `-- ${kind}` : `${p}% ${kind}`);
    paint(
      MARGIN,
      'G7 - Use windows',
      [k.useLastMonth, k.useLastWeek, k.useMonthToDate, k.useThisWeek],
      [0.36, 0.56, 0.98],
      `WoW ${fmt(k.useWowPct, '')} this vs last week`.trim(),
      `MoM ${fmt(k.useMomPct, '')} MTD vs last month`.trim()
    );
    paint(
      MARGIN + cardW + gap,
      'G8 - Visits windows',
      [k.visitsLastMonth, k.visitsLastWeek, k.visitsMonthToDate, k.visitsThisWeek],
      [0.96, 0.74, 0.09],
      `WoW ${fmt(k.visitsWowPct, '')} this vs last week`.trim(),
      `MoM ${fmt(k.visitsMomPct, '')} MTD vs last month`.trim()
    );
    this.y = top - cardH - 12;
  }

  table(headers: string[], rows: string[][], colWeights?: number[]) {
    const weights = colWeights ?? headers.map(() => 1);
    const sum = weights.reduce((a, b) => a + b, 0);
    const widths = weights.map((w) => (CONTENT_W * w) / sum);
    const rowH = 13;
    const headerH = 15;
    this.ensure(headerH + rows.length * rowH + 16);
    const estH = headerH + rows.length * rowH + 8;
    const top = this.y;
    this.content.push(
      rgbOp(COL.white),
      `${MARGIN} ${top - estH} ${CONTENT_W} ${estH} re`,
      'f',
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} ${top - estH} ${CONTENT_W} ${estH} re`,
      'S'
    );
    let y = top;
    const draw = (cells: string[], yy: number, header: boolean) => {
      if (header) {
        this.content.push(
          '0.92 0.93 0.95 rg',
          `${MARGIN} ${yy - headerH + 3} ${CONTENT_W} ${headerH} re`,
          'f'
        );
      }
      let x = MARGIN;
      cells.forEach((cell, i) => {
        this.text(x + 3, yy - 9, cell.length > 36 ? `${cell.slice(0, 34)}...` : cell, 8, header);
        x += widths[i];
      });
    };
    draw(headers, y, true);
    y -= headerH;
    rows.forEach((row, idx) => {
      if (idx % 2 === 1) {
        this.content.push(rgbOp(COL.zebra), `${MARGIN} ${y - rowH + 3} ${CONTENT_W} ${rowH} re`, 'f');
      }
      draw(row, y, false);
      y -= rowH;
    });
    this.y = y - 10;
  }

  reservedBox() {
    this.ensure(50);
    const h = 44;
    const top = this.y;
    this.content.push(
      '0.94 0.95 0.97 rg',
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'f',
      rgbOp(COL.line, 'RG'),
      '0.5 w',
      `${MARGIN} ${top - h} ${CONTENT_W} ${h} re`,
      'S'
    );
    this.text(MARGIN + 10, top - 16, 'G1-G8 included. Live table is latest week only.', 9, true, COL.muted);
    this.text(
      MARGIN + 10,
      top - 30,
      'WoW = this week vs last week. MoM = MTD vs last month - never this week vs last month.',
      8,
      false,
      COL.soft
    );
    this.y = top - h - 10;
  }

  finish(): Blob {
    this.flush();
    const pageCount = this.pages.length;
    type Obj = { id: number; body: string };
    const objs: Obj[] = [];
    const catalogId = 1;
    const pagesId = 2;
    const fontRegular = 3;
    const fontBold = 4;
    let nextId = 5;
    objs.push({ id: fontRegular, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>' });
    objs.push({ id: fontBold, body: '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>' });
    const contentIds: number[] = [];
    for (let i = 0; i < pageCount; i++) {
      const id = nextId++;
      contentIds.push(id);
      const stream = this.pages[i];
      objs.push({ id, body: `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream` });
    }
    const pageIds: number[] = [];
    for (let i = 0; i < pageCount; i++) {
      const id = nextId++;
      pageIds.push(id);
      objs.push({
        id,
        body: `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Contents ${contentIds[i]} 0 R /Resources << /Font << /F1 ${fontRegular} 0 R /F2 ${fontBold} 0 R >> >> >>`,
      });
    }
    objs.push({
      id: pagesId,
      body: `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageCount} >>`,
    });
    objs.push({ id: catalogId, body: `<< /Type /Catalog /Pages ${pagesId} 0 R >>` });
    objs.sort((a, b) => a.id - b.id);
    const maxId = objs[objs.length - 1].id;
    let pdf = '%PDF-1.4\n';
    const offsets: number[] = [0];
    for (let i = 1; i <= maxId; i++) {
      const obj = objs.find((o) => o.id === i)!;
      offsets[i] = pdf.length;
      pdf += `${i} 0 obj\n${obj.body}\nendobj\n`;
    }
    const xrefPos = pdf.length;
    pdf += `xref\n0 ${maxId + 1}\n0000000000 65535 f \n`;
    for (let i = 1; i <= maxId; i++) {
      pdf += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
    }
    pdf += `trailer\n<< /Size ${maxId + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefPos}\n%%EOF`;
    return new Blob([pdf], { type: 'application/pdf' });
  }
}

export function buildPdPdfFilename(casino: string, fromStart: string, toStart: string): string {
  const safe = (s: string) =>
    (s || 'Casino')
      .replace(/[^\w\-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 48) || 'Casino';
  return `PD_${safe(casino)}_${fromStart}_${toStart}.pdf`;
}

export function buildPlayerDevelopmentPdf(input: PdPdfInput): Blob {
  const doc = new PdPdfDoc();
  const filename = buildPdPdfFilename(input.casinoLabel, (input as PdPdfInput & { fromWeekStart?: string }).fromWeekStart || input.fromWeek, (input as PdPdfInput & { toWeekStart?: string }).toWeekStart || input.toWeek);
  const range = `${input.fromWeek} to ${input.toWeek} UTC`;
  const when = `Generated ${input.generatedAt.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
  doc.setMeta(filename, input.casinoLabel, range);

  doc.newPage(false);
  doc.mainHeader(input.casinoLabel, range, when);

  doc.section('Report scope', 'Filters applied to every chart and total below.');
  doc.scopeRow([
    { label: 'Casino', value: input.casinoLabel },
    { label: 'Weeks (UTC)', value: `${input.fromWeek} - ${input.toWeek}` },
    { label: 'Age bands', value: input.ageLabel },
    { label: 'Gender', value: input.genderLabel },
  ]);

  doc.section('At a glance', 'Latest-week KPIs for the selected casino and filters.');
  doc.kpiRow([
    {
      label: 'Players',
      value: fmtInt(input.kpis.players),
      hint: 'Unique players in latest week.',
    },
    {
      label: 'Active Use & Visit',
      value: fmtInt(input.kpis.activeUseAndVisit),
      hint: 'use>0 and visits>0.',
    },
    {
      label: 'Use',
      value: fmtInt(input.kpis.usedThisWeek),
      hint: `users with use>0 · sum ${fmtInt(input.kpis.useThisWeek)}`,
    },
    {
      label: 'Visits',
      value: fmtInt(input.kpis.visitedThisWeek),
      hint: `users with visits>0 · sum ${fmtInt(input.kpis.visitsThisWeek)}`,
    },
    {
      label: 'New Users',
      value: fmtInt(input.kpis.newUsers),
      hint: 'First week in casino.',
    },
  ]);

  doc.section(
    'Trends',
    'G1-G4 use the full week range. G5 / G7 / G8 are latest week.'
  );
  doc.trendsGrid(input.weekSeries, input.ageBands, input.bandColors);
  const g5Views = input.g5Views?.length
    ? input.g5Views
    : [];
  g5Views.forEach((view) => {
    doc.mixDonut(input.g5Week, view.slices, view.label);
  });

  doc.section('G7 Use windows / G8 Visits windows', 'Latest-week snapshot windows (layout only in PDF bars).');
  doc.windowPair(input.kpis);

  doc.newPage(true);
  doc.section(
    'Week summary (range)',
    'Compact rollup - player-week roster lives on CSV / Excel Detail'
  );
  const rollup = [...input.weekSeries].reverse();
  doc.table(
    ['Week', 'Use', 'Visits', 'Active Use & Visit', 'New Users'],
    rollup.map((m) => [
      m.week,
      fmtInt(m.use),
      fmtInt(m.visits),
      fmtInt(m.activeUseAndVisit),
      fmtInt(m.newUsers),
    ]),
    [1.1, 1.0, 1.0, 1.3, 1.1]
  );

  doc.section('G7 / G8 snapshot windows', 'Sums from latest-week fields. WoW = this week vs last week. MoM = MTD vs last month.');
  const k = input.kpis;
  const fmtPct = (pct: number | null) => (pct == null ? '--' : `${pct}%`);
  doc.table(
    ['Window', 'Use', 'Visits'],
    [
      ['Last Month', fmtInt(k.useLastMonth), fmtInt(k.visitsLastMonth)],
      ['Last Week', fmtInt(k.useLastWeek), fmtInt(k.visitsLastWeek)],
      ['Month to date', fmtInt(k.useMonthToDate), fmtInt(k.visitsMonthToDate)],
      ['This week', fmtInt(k.useThisWeek), fmtInt(k.visitsThisWeek)],
      ['WoW', fmtPct(k.useWowPct), fmtPct(k.visitsWowPct)],
      ['MoM (MTD vs last month)', fmtPct(k.useMomPct), fmtPct(k.visitsMomPct)],
    ],
    [1.6, 1.0, 1.0]
  );

  doc.section('Metric definitions', 'How to read this snapshot');
  doc.table(
    ['Term', 'Definition'],
    [
      ['Players', 'Unique player-weeks in the latest week on screen.'],
      ['Active Use & Visit', 'Players with use>0 AND visits>0 in the same week.'],
      ['Use', 'Users with use>0. Volume is the Use This Week sum.'],
      ['Visits', 'Users with visits>0. Volume is the Visits This Week sum.'],
      ['New Users', 'First observed week at this casino.'],
      ['Weeks', 'UTC ISO weeks (Monday start). Query tokens are YYYY-Www.'],
      ['Mix (G5)', 'Age / Gender / Gender-by-age composition for the latest week.'],
      ['G7 / G8', 'Last Month, Last Week, MTD, This week. WoW = this vs last week. MoM = MTD vs last month.'],
    ],
    [1.3, 2.7]
  );

  doc.reservedBox();

  return doc.finish();
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
